import { Injectable } from '@angular/core';
// Type-only imports — no runtime cost. The actual ExcelJS / file-saver
// modules are loaded with dynamic `await import(...)` only when an export
// is actually triggered. Saves ~700 kB from the eagerly-loaded bundle.
import type * as ExcelJSType from 'exceljs';

// Lazy-loaded pdfMake to avoid initialization errors
let pdfMake: any = null;
let pdfMakeInitialized = false;

async function initPdfMake() {
  if (pdfMakeInitialized) return pdfMake;
  
  try {
    const pdfMakeModule = await import('pdfmake/build/pdfmake');
    const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
    
    pdfMake = pdfMakeModule.default || pdfMakeModule;
    const pdfFonts = pdfFontsModule.default || pdfFontsModule;
    
    // Set up vfs fonts
    if (pdfFonts && (pdfFonts as any).pdfMake && (pdfFonts as any).pdfMake.vfs) {
      (pdfMake as any).vfs = (pdfFonts as any).pdfMake.vfs;
    } else if (pdfFonts && (pdfFonts as any).vfs) {
      (pdfMake as any).vfs = (pdfFonts as any).vfs;
    }
    
    pdfMakeInitialized = true;
    return pdfMake;
  } catch (error) {
    console.error('Failed to load pdfMake:', error);
    throw error;
  }
}

import {
  GroupLevel,
  PdfOptions,
  ReportColumn,
  ReportDefinition,
} from './report.models';

@Injectable({ providedIn: 'root' })
export class ReportExportService {
  // Cache for the lazy-loaded ExcelJS module. First export pays the load
  // cost (~700 kB on the wire); subsequent exports reuse the same module.
  private excelLib: typeof ExcelJSType | null = null;
  private fileSaverLib: { saveAs: (data: Blob, filename: string) => void } | null = null;

  private async getExcelLib(): Promise<typeof ExcelJSType> {
    if (!this.excelLib) {
      // CJS modules can land under `.default` when imported dynamically,
      // depending on bundler/tsconfig combo. Mirror the pdfMake pattern.
      const mod: any = await import('exceljs');
      this.excelLib = (mod.default || mod) as typeof ExcelJSType;
    }
    return this.excelLib;
  }
  private async getSaveAs(): Promise<(data: Blob, filename: string) => void> {
    if (!this.fileSaverLib) {
      const mod: any = await import('file-saver');
      const fn = mod.saveAs || mod.default?.saveAs || mod.default;
      this.fileSaverLib = { saveAs: fn };
    }
    return this.fileSaverLib.saveAs;
  }

  /** Simple Excel export without grouping. */
  async exportExcel<T>(filename: string, columns: ReportColumn<T>[], rows: T[]) {
    const ExcelJS = await this.getExcelLib();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Report');

    // Add header row
    const headerRow = worksheet.addRow(columns.map((c) => c.label));
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE9ECEF' }
    };

    // Add data rows
    rows.forEach((r) => {
      const rowData = columns.map((c) => {
        const raw = this.getValue(r, c.field);
        // Keep numbers as numbers for Excel formatting
        if (typeof raw === 'number') {
          return raw;
        }
        return c.format ? c.format(r, raw) : raw;
      });
      const dataRow = worksheet.addRow(rowData);
      
      // Format numeric cells with Norwegian number format
      dataRow.eachCell((cell, colNumber) => {
        if (typeof cell.value === 'number') {
          cell.numFmt = '#,##0.00';  // Norwegian number format
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
        }
      });
    });

    // Auto-fit columns
    worksheet.columns.forEach((column, index) => {
      if (column) {
        const col = columns[index];
        column.width = col?.width ? Number(col.width) / 7 : 15; // Estimate width
      }
    });

    // Generate file and download
    const buffer = await workbook.xlsx.writeBuffer();
    const saveAs = await this.getSaveAs();
    saveAs(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      this.ensureExt(filename, '.xlsx')
    );
  }

  /** Simple A4 PDF export without grouping. */
  async exportPdfA4<T>(
    filename: string,
    columns: ReportColumn<T>[],
    rows: T[],
    opts: PdfOptions = {}
  ) {
    const pdf = await initPdfMake();
    const fontSize = opts.fontSize ?? 9;
    const widths = columns.map((c) => c.width ?? 'auto');
    const body = [
      columns.map((c) => ({ text: c.label, style: 'tableHeader' })),
      ...rows.map((r) =>
        columns.map((c) => ({
          text: String(
            c.format
              ? c.format(this.getValue(r, c.field), r)
              : this.getValue(r, c.field) ?? ''
          ),
          alignment: c.align ?? 'left',
          fontSize,
        }))
      ),
    ];

    const content: any[] = [];
    if (opts.logoDataUrl || opts.title || opts.subtitle) {
      content.push({
        columns: [
          opts.logoDataUrl
            ? { image: opts.logoDataUrl, fit: [90, 40], margin: [0, 0, 10, 0] }
            : {},
          {
            stack: [
              opts.title ? { text: opts.title, style: 'title' } : undefined,
              opts.subtitle
                ? { text: opts.subtitle, style: 'subtitle' }
                : undefined,
            ].filter(Boolean),
          },
        ],
        columnGap: 12,
        margin: [0, 0, 0, 10],
      });
    }
    content.push({
      table: { headerRows: 1, widths, body },
      layout: {
        fillColor: (rowIndex: number) => (rowIndex === 0 ? '#F2F2F2' : null),
        hLineColor: () => '#DDD',
        vLineColor: () => '#EEE',
      },
    });

    const doc: any = {
      pageSize: 'A4',
      pageOrientation: opts.landscape ? 'landscape' : 'portrait',
      pageMargins: opts.margin ?? [28, 36, 28, 36],
      defaultStyle: { fontSize },
      content,
      styles: {
        title: { fontSize: 14, bold: true, margin: [0, 0, 0, 2] },
        subtitle: { fontSize: 10, color: '#666' },
        tableHeader: { bold: true, fontSize: fontSize + 1 },
      },
      footer: (currentPage: number, pageCount: number) => ({
        columns: [
          {
            text: opts.footerLeft ?? '',
            alignment: 'left',
            margin: [28, 5, 28, 0],
            fontSize: 8,
          },
          {
            text:
              opts.pageNumbers === false ? '' : `${currentPage} / ${pageCount}`,
            alignment: 'center',
            margin: [0, 5, 0, 0],
            fontSize: 8,
          },
          {
            text: opts.footerRight ?? '',
            alignment: 'right',
            margin: [28, 5, 28, 0],
            fontSize: 8,
          },
        ],
      }),
    };

    (pdf as any).createPdf(doc).download(this.ensureExt(filename, '.pdf'));
  }

  // ---------------------------
  // GROUPING + TOTALS UTILITIES
  // ---------------------------

  /** Build a grouping tree + grand totals from rows and group definition. Used by UI and exports. */
  buildGroups<T>(rows: T[], groups: GroupLevel[], columns: ReportColumn<T>[]) {
    const aggCols = new Map(
      columns.filter((c) => c.aggregate).map((c) => [c.field, c.aggregate!])
    );
    const get = (o: any, p: string) =>
      p.split('.').reduce((x, k) => (x ? x[k] : undefined), o);

    type Node = {
      key: any;
      label: string;
      level: number;
      rows: T[];
      children: Node[];
      subtotal?: Record<string, number>;
      pageBreakAfter?: boolean;
    };

    const aggregate = (rowsLocal: T[], fields: string[]) => {
      const res: Record<string, number> = {};
      for (const f of fields) {
        const fn = aggCols.get(f);
        if (!fn) continue;
        const vals = rowsLocal.map((r) => +get(r, f) || 0);
        switch (fn) {
          case 'sum':
            res[f] = vals.reduce((a, b) => a + b, 0);
            break;
          case 'count':
            res[f] = rowsLocal.length;
            break;
          case 'avg':
            res[f] = vals.length
              ? vals.reduce((a, b) => a + b, 0) / vals.length
              : 0;
            break;
          case 'min':
            res[f] = vals.length ? Math.min(...vals) : 0;
            break;
          case 'max':
            res[f] = vals.length ? Math.max(...vals) : 0;
            break;
        }
      }
      return res;
    };

    const makeNode = (key: any, level: number, g?: GroupLevel): Node => ({
      key,
      label: g?.label ? g.label(key) : String(key ?? ''),
      level,
      rows: [],
      children: [],
      pageBreakAfter: g?.pageBreakAfter ?? false,
    });

    const rec = (data: T[], level: number): Node[] => {
      if (level >= groups.length) {
        // Leaf: no more grouping levels
        return [
          {
            key: null,
            label: '',
            level,
            rows: data.slice(),
            children: [],
          } as Node,
        ];
      }
      const g = groups[level];
      const bucket = new Map<any, T[]>();
      for (const r of data) {
        const k = get(r, g.field);
        const arr = bucket.get(k) ?? [];
        arr.push(r);
        bucket.set(k, arr);
      }
      const nodes: Node[] = [];
      for (const [k, arr] of bucket.entries()) {
        const node = makeNode(k, level, g);
        if (level < groups.length - 1) {
          node.children = rec(arr, level + 1);
        } else {
          node.rows = arr;
        }
        const subFields = g.subtotalFor ?? [];
        const aggregateSource = node.rows.length
          ? node.rows
          : node.children.flatMap((c) => c.rows);
        node.subtotal = aggregate(aggregateSource, subFields);
        nodes.push(node);
      }
      // Default alphabetical sort on key
      return nodes.sort((a, b) =>
        (a.key ?? '').toString().localeCompare((b.key ?? '').toString())
      );
    };

    const tree = rec(rows, 0);
    const grandTotal = (() => {
      const fields = columns.filter((c) => c.aggregate).map((c) => c.field);
      return aggregate(rows, fields);
    })();

    return { tree, grandTotal };
  }

  /** Grouped PDF export (with per-group tables + subtotal rows, and optional page breaks). */
  async exportPdfA4Grouped<T>(
    filename: string,
    def: ReportDefinition<T>,
    rows: T[],
    opts: PdfOptions = {}
  ) {
    const pdf = await initPdfMake();
    const fontSize = opts.fontSize ?? 9;
    const { columns, groups = [], grandTotalFor = [] } = def;
    const widths = columns.map((c) => c.width ?? 'auto');
    const { tree, grandTotal } = this.buildGroups(rows, groups, columns);

    const sections: any[] = [];

    // Header (logo/title/subtitle)
    if (opts.logoDataUrl || opts.title || opts.subtitle) {
      sections.push({
        columns: [
          opts.logoDataUrl
            ? { image: opts.logoDataUrl, fit: [90, 40], margin: [0, 0, 10, 0] }
            : {},
          {
            stack: [
              opts.title ? { text: opts.title, style: 'title' } : undefined,
              opts.subtitle
                ? { text: opts.subtitle, style: 'subtitle' }
                : undefined,
            ].filter(Boolean),
          },
        ],
        columnGap: 12,
        margin: [0, 0, 0, 10],
      });
    }

    const headerRow = columns.map((c) => ({
      text: c.label,
      style: 'tableHeader',
    }));

    // Render a simple table for a set of rows
    const renderTable = (rowsLocal: T[]) => ({
      table: {
        headerRows: 1,
        widths,
        body: [
          headerRow,
          ...rowsLocal.map((r) =>
            columns.map((c) => ({
              text: String(
                c.format
                  ? c.format(this.getValue(r, c.field), r)
                  : this.getValue(r, c.field) ?? ''
              ),
              alignment: c.align ?? 'left',
              fontSize,
            }))
          ),
        ],
      },
      layout: {
        fillColor: (rowIndex: number) => (rowIndex === 0 ? '#F2F2F2' : null),
        hLineColor: () => '#DDD',
        vLineColor: () => '#EEE',
      },
      margin: [0, 2, 0, 6],
    });

    // Render a "subtotal" single-row table for clarity
    const renderSubtotalRow = (
      label: string,
      totals: Record<string, number>
    ) => {
      const cells = columns.map((c, idx) => {
        if (idx === 0) return { text: label, bold: true, fontSize };
        if (totals[c.field] != null)
          return {
            text: String(this.formatNumber(totals[c.field])),
            bold: true,
            alignment: c.align ?? 'right',
            fontSize,
          };
        return { text: '', fontSize };
      });
      return {
        table: { headerRows: 0, widths, body: [cells] },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          fillColor: '#EFEFEF',
        },
        margin: [0, 0, 0, 8],
      };
    };

    // Depth-first traversal of the group tree
    const walk = (nodes: any[], level = 0) => {
      for (const n of nodes) {
        // Group header
        if (groups[level]) {
          const label = groups[level].label
            ? groups[level].label!(n.key)
            : `${groups[level].field}: ${n.key ?? ''}`;
          sections.push({
            text: label,
            style: 'groupHdr',
            margin: [0, level ? 6 : 0, 0, 2],
          });
        }
        // Children then rows
        if (n.children?.length) walk(n.children, level + 1);
        if (n.rows?.length) sections.push(renderTable(n.rows));
        // Subtotal
        if (n.subtotal && (groups[level]?.subtotalFor?.length ?? 0) > 0) {
          const label = `Sum ${
            groups[level].label ? groups[level].label!(n.key) : n.key ?? ''
          }`;
          sections.push(renderSubtotalRow(label, n.subtotal));
        }
        // Optional page break
        if (n.pageBreakAfter) sections.push({ text: '', pageBreak: 'after' });
      }
    };

    if (groups.length) walk(tree);
    else sections.push(renderTable(rows));

    // Grand total row
    if (grandTotalFor.length) {
      const gt: Record<string, number> = {};
      for (const f of grandTotalFor) gt[f] = grandTotal[f] ?? 0;
      sections.push(renderSubtotalRow('Total', gt));
    }

    const doc: any = {
      pageSize: 'A4',
      pageOrientation: opts.landscape ? 'landscape' : 'portrait',
      pageMargins: opts.margin ?? [28, 36, 28, 36],
      defaultStyle: { fontSize },
      content: sections,
      styles: {
        title: { fontSize: 14, bold: true, margin: [0, 0, 0, 2] },
        subtitle: { fontSize: 10, color: '#666' },
        tableHeader: { bold: true, fontSize: fontSize + 1 },
        groupHdr: { bold: true, fontSize: fontSize + 1, color: '#0F4C81' },
      },
      footer: (currentPage: number, pageCount: number) => ({
        columns: [
          {
            text: opts.footerLeft ?? '',
            alignment: 'left',
            margin: [28, 5, 28, 0],
            fontSize: 8,
          },
          {
            text:
              opts.pageNumbers === false ? '' : `${currentPage} / ${pageCount}`,
            alignment: 'center',
            margin: [0, 5, 0, 0],
            fontSize: 8,
          },
          {
            text: opts.footerRight ?? '',
            alignment: 'right',
            margin: [28, 5, 28, 0],
            fontSize: 8,
          },
        ],
      }),
    };

    (pdf as any).createPdf(doc).download(this.ensureExt(filename, '.pdf'));
  }

  /** Grouped Excel export (single sheet with group markers and subtotals). */
  async exportExcelGrouped<T>(filename: string, def: ReportDefinition<T>, rows: T[]) {
    const { columns, groups = [] } = def;
    const ExcelJS = await this.getExcelLib();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Report');

    if (!groups.length) {
      // Fallback to simple export
      await this.exportExcel(filename, columns, rows);
      return;
    }

    const { tree } = this.buildGroups(rows, groups, columns);

    // Add header row
    const headerRow = worksheet.addRow(columns.map((c) => c.label));
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE9ECEF' }
    };

    const pushGroup = (nodes: any[], level = 0) => {
      for (const n of nodes) {
        // Group header row
        const groupRow = worksheet.addRow([
          `${'  '.repeat(level)}${
            groups[level].label ? groups[level].label!(n.key) : n.key
          }`,
          '', '', ''
        ]);
        groupRow.font = { bold: true };
        
        if (n.rows?.length) {
          for (const r of n.rows) {
            worksheet.addRow(
              columns.map((c) => {
                const raw = this.getValue(r, c.field);
                return c.format ? c.format(raw, r) : raw;
              })
            );
          }
        }
        if (n.children?.length) pushGroup(n.children, level + 1);
        if (n.subtotal) {
          const subtotalRow = worksheet.addRow([
            `Sum ${n.label}`,
            ...columns.slice(1).map((c) => n.subtotal[c.field] ?? '')
          ]);
          subtotalRow.font = { bold: true };
        }
      }
    };
    pushGroup(tree);

    // Auto-fit columns
    worksheet.columns.forEach((column, index) => {
      if (column) {
        const col = columns[index];
        column.width = col?.width ? Number(col.width) / 7 : 15;
      }
    });

    // Generate file and download
    const buffer = await workbook.xlsx.writeBuffer();
    const saveAs = await this.getSaveAs();
    saveAs(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      this.ensureExt(filename, '.xlsx')
    );
  }

  // -------------
  // Small helpers
  // -------------
  private getValue(obj: any, path: string): any {
    if (!obj || !path) return null;
    return path.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
  }
  private ensureExt(name: string, ext: string) {
    return name?.toLowerCase().endsWith(ext) ? name : `${name}${ext}`;
  }
  private formatNumber(n: number) {
    try {
      return new Intl.NumberFormat('no-NO', {
        maximumFractionDigits: 2,
      }).format(n);
    } catch {
      return (Math.round(n * 100) / 100).toString();
    }
  }
}
